import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@mui/material";
import axios from "axios";
import { useDispatch } from "react-redux";
import { setGlobalAlert } from "../../store/actions";

// material-ui
import { useTheme } from "@mui/material/styles";
import {
    Avatar,
    Box,
    Badge,
    ButtonBase,
    CardActions,
    ClickAwayListener,
    Divider,
    Grid,
    Grow,
    Paper,
    Popper,
    TextField,
    Typography,
    useMediaQuery,
} from "@mui/material";

// third-party
import PerfectScrollbar from "react-perfect-scrollbar";

// project imports
import MainCard from "../../components/MainCard";
import Transitions from "../../components/extended/Transitions";
//import NotificationList from './NotificationList';

// assets
import { IconBell } from "@tabler/icons";

const mockNotifications = [
    {
        title: "ORDER FILLED",
        description: "Your limit long order for AAPL has been filled at 190.36.",
        status: "read",
    },
    {
        title: "Update Available",
        description: "A new update for your app is available.",
        status: "unread",
    },
    {
        title: "Payment Received",
        description: "Payment of $100 has been received.",
        status: "unread",
    },
    {
        title: "Pagination Test",
        description: "Payment of $100 has been received.",
        status: "unread",
    },
    {
        title: "Pagination Test",
        description: "Payment of $100 has been received.",
        status: "unread",
    },
];

// notification status options
const status = [
    {
        value: "unread",
        label: "Unread",
    },
    {
        value: "read",
        label: "Read",
    },
];

async function getAllNotifications(dispatch) {
    try {
        const token = sessionStorage.getItem("token");
        const config = {
            headers: { Authorization: `Bearer ${token}` },
        };
        const response = await axios.get(
            `${process.env.REACT_APP_SERVER_URL}/notifications/getAllNotifications`,
            config
        );
        return response.data;
    } catch (err) {
        console.log(err);
        if (dispatch) {
            dispatch(setGlobalAlert({
                message: "Failed to fetch notifications.",
                severity: "error"
            }));
        }
        return [];
    }
}

async function markAllNotificationsAsRead(dispatch) {
    try {
        const token = sessionStorage.getItem("token");
        const config = {
            headers: { Authorization: `Bearer ${token}` },
        };
        await axios.get(
            `${process.env.REACT_APP_SERVER_URL}/notifications/markAllNotificationsAsRead`,
            config
        );
    } catch (err) {
        console.log(err);
        if (dispatch) {
            dispatch(setGlobalAlert({
                message: "Failed to mark notifications as read.",
                severity: "error"
            }));
        }
        return [];
    }
}

// ==============================|| NOTIFICATION ||============================== //

const NotificationSection = () => {
    const theme = useTheme();
    const matchesXs = useMediaQuery(theme.breakpoints.down("md"));
    const dispatch = useDispatch();

    const [open, setOpen] = useState(false);
    const [value, setValue] = useState("unread");
    const [notifications, setNotifications] = useState([]);
    const [showMarkAllAsRead, setShowMarkAllAsRead] = useState(false);

    const unreadCount = notifications.filter(
        (notification) => notification.status === "unread"
    ).length;

    useEffect(() => {
        setShowMarkAllAsRead(unreadCount > 0);
    }, [notifications]);

    // State for pagination
    const itemsPerPage = 3;
    const [currentPage, setCurrentPage] = useState(1);
    /**
     * anchorRef is used on different componets and specifying one type leads to other components throwing an error
     * */
    const anchorRef = useRef(null);

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const handleClose = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }
        setOpen(false);
    };

    const prevOpen = useRef(open);
    useEffect(() => {
        if (prevOpen.current === true && open === false) {
            anchorRef.current.focus();
        }
        prevOpen.current = open;
    }, [open]);

    // Filter notifications based on the selected value
    const filteredNotifications = notifications.filter(
        (notification) => notification.status === value
    );

    // Calculate total pages
    const totalPages = Math.ceil(filteredNotifications.length / itemsPerPage);

    // Get notifications for the current page
    const currentNotifications = filteredNotifications.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const handleChange = (event) => {
        if (event?.target.value) {
            setValue(event?.target.value);
            setCurrentPage(1); // Reset the currentPage to 1
            // Update the showMarkAllAsRead state based on the dropdown value
            setShowMarkAllAsRead(event?.target.value === "unread" && unreadCount > 0);
        }
    };

    const markAllAsReadHeight = showMarkAllAsRead ? 30 : 0; // Assuming 40px height for the "Mark all as read" component

    const cardHeight =
        currentNotifications.length === 3
            ? `calc(450px + ${markAllAsReadHeight}px)`
            : currentNotifications.length === 2
            ? `calc(360px + ${markAllAsReadHeight}px)`
            : currentNotifications.length === 1
            ? `calc(270px + ${markAllAsReadHeight}px)`
            : `calc(180px + ${markAllAsReadHeight}px)`;

    const markAllAsRead = async () => {
        await markAllNotificationsAsRead(dispatch); // Mark all notifications as read in the backend
        const updatedNotifications = await getAllNotifications(dispatch); // Fetch the updated notifications
        setNotifications(updatedNotifications); // Update the state with the fetched notifications
        setShowMarkAllAsRead(false); // Hide the "Mark all as read" button
        setValue("unread"); // Set the filter value to "unread"
    };

    useEffect(() => {
        async function fetchData() {
            const fetchedNotifications = await getAllNotifications(dispatch);
            setNotifications(fetchedNotifications);
        }

        fetchData();
    }, [dispatch]);

    return (
        <>
            <Box
                sx={{
                    ml: 2,
                    mr: 1,
                    [theme.breakpoints.down("md")]: {
                        mr: 2,
                    },
                }}
            >
                <Badge
                    badgeContent={unreadCount}
                    color="error"
                    max={99}
                    anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "right",
                    }}
                    transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                    }}
                    sx={{
                        "& .MuiBadge-badge": {
                            height: "16px",
                            minWidth: "16px",
                            fontSize: "10px",
                            padding: "0 4px",
                        },
                    }}
                >
                    <ButtonBase sx={{ borderRadius: "12px" }}>
                        <Avatar
                            variant="rounded"
                            sx={{
                                ...theme.typography.commonAvatar,
                                ...theme.typography.mediumAvatar,
                                transition: "all .2s ease-in-out",
                                background: theme.palette.secondary.light,
                                color: theme.palette.secondary.dark,
                                '&[aria-controls="menu-list-grow"],&:hover': {
                                    background: theme.palette.secondary.dark,
                                    color: theme.palette.secondary.light,
                                },
                            }}
                            ref={anchorRef}
                            aria-controls={open ? "menu-list-grow" : undefined}
                            aria-haspopup="true"
                            onClick={handleToggle}
                            color="inherit"
                        >
                            <IconBell stroke={1.5} size="21px" />
                        </Avatar>
                    </ButtonBase>
                </Badge>
            </Box>

            <Popper
                // key={value} // Add this line to re-render the Popper component when the filter value changes
                placement={matchesXs ? "bottom" : "bottom-end"}
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
                popperOptions={{
                    modifiers: [
                        {
                            name: "flip",
                            enabled: false, // This will prevent the Popper from flipping its position
                        },
                        {
                            name: "offset",
                            options: {
                                offset: [matchesXs ? 5 : 0, 20],
                            },
                        },
                    ],
                }}
            >
                {({ TransitionProps }) => (
                    <Grow position={matchesXs ? "top" : "top-right"} in={open} {...TransitionProps}>
                        <Paper>
                            <ClickAwayListener onClickAway={handleClose}>
                                <MainCard
                                    border={false}
                                    elevation={16}
                                    content={false}
                                    boxShadow
                                    shadow={theme.shadows[16]}
                                    sx={{
                                        width: "400px", // Fixed width
                                        height: cardHeight, // Fixed height
                                    }}
                                >
                                    <Grid container direction="column" spacing={2}>
                                        <Grid item xs={12}>
                                            <Grid
                                                container
                                                alignItems="center"
                                                justifyContent="center"
                                                sx={{ pt: 2, px: 2 }}
                                            >
                                                <Typography variant="subtitle1">
                                                    Notifications
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <PerfectScrollbar
                                                style={{
                                                    height: "calc(100% - 60px)",
                                                    overflowX: "hidden",
                                                }}
                                            >
                                                <Grid container direction="column" spacing={2}>
                                                    <Grid item xs={12}>
                                                        <Box
                                                            sx={{
                                                                px: 2,
                                                                pt: 0.25,
                                                            }}
                                                        >
                                                            <TextField
                                                                id="outlined-select-currency-native"
                                                                select
                                                                fullWidth
                                                                value={value}
                                                                onChange={handleChange}
                                                                SelectProps={{
                                                                    native: true,
                                                                }}
                                                            >
                                                                {status.map((option) => (
                                                                    <option
                                                                        key={option.value}
                                                                        value={option.value}
                                                                        onClick={() =>
                                                                            setValue(option.value)
                                                                        }
                                                                    >
                                                                        {option.label}
                                                                    </option>
                                                                ))}
                                                            </TextField>
                                                        </Box>
                                                    </Grid>
                                                    <Grid item xs={12} p={0}>
                                                        <Divider sx={{ my: 0 }} />
                                                    </Grid>
                                                    {/* Render mock notifications as cards */}
                                                    {currentNotifications.length === 0 ? (
                                                        <Grid item xs={12}>
                                                            <Typography
                                                                variant="subtitle1"
                                                                align="center"
                                                            >
                                                                {`You have no ${
                                                                    value === "unread"
                                                                        ? "unread"
                                                                        : "read"
                                                                } notifications.`}
                                                            </Typography>
                                                        </Grid>
                                                    ) : (
                                                        // Render mock notifications as cards
                                                        currentNotifications.map(
                                                            (notification, index) => (
                                                                <Grid
                                                                    item
                                                                    xs={12}
                                                                    //key={notification.id}
                                                                    key={index}
                                                                    sx={{
                                                                        height: "calc(100% / 3.2)",
                                                                        marginBottom:
                                                                            index ===
                                                                            currentNotifications.length -
                                                                                1
                                                                                ? "5px"
                                                                                : "0px",
                                                                    }}
                                                                >
                                                                    <Paper elevation={3}>
                                                                        <Box p={2}>
                                                                            <Typography variant="h6">
                                                                                {notification.title}
                                                                            </Typography>
                                                                            <Typography variant="body2">
                                                                                {
                                                                                    notification.description
                                                                                }
                                                                            </Typography>
                                                                        </Box>
                                                                    </Paper>
                                                                </Grid>
                                                            )
                                                        )
                                                    )}
                                                </Grid>
                                            </PerfectScrollbar>
                                            {/* Conditionally render Pagination controls */}
                                            {currentNotifications.length > 0 && (
                                                <Box display="flex" justifyContent="center" mt={2}>
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        disabled={currentPage === 1}
                                                        onClick={() =>
                                                            setCurrentPage((prev) =>
                                                                Math.max(prev - 1, 1)
                                                            )
                                                        }
                                                    >
                                                        Previous
                                                    </Button>
                                                    <Typography variant="subtitle2" sx={{ mx: 2 }}>
                                                        Page {currentPage} out of {totalPages || 1}
                                                    </Typography>
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        disabled={
                                                            currentPage === totalPages ||
                                                            totalPages === 0
                                                        }
                                                        onClick={() =>
                                                            setCurrentPage((prev) =>
                                                                Math.min(prev + 1, totalPages)
                                                            )
                                                        }
                                                    >
                                                        Next
                                                    </Button>
                                                </Box>
                                            )}
                                        </Grid>
                                    </Grid>
                                    <Divider />
                                    <CardActions
                                        sx={{
                                            p: 1.25,
                                            justifyContent: "center",
                                        }}
                                    >
                                        {/* Conditionally render "Mark all as read" */}
                                        {showMarkAllAsRead && (
                                            <Typography
                                                component={Link}
                                                to="#"
                                                variant="subtitle2"
                                                color="primary"
                                                onClick={markAllAsRead}
                                            >
                                                Mark all as read
                                            </Typography>
                                        )}
                                    </CardActions>
                                </MainCard>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
        </>
    );
};

export default NotificationSection;
